import { fetch } from 'http-common';

import { CatalogueMenu, ProductType } from 'types/api/product.type';

export type ProductParams = {
  season: string;
  marketLine?: string;
  warehouse: string;
  zone: string;
  category?: string;
  genre?: string;
  itemCode?: string;
  mainFabricCode?: string;
  itemColorCode?: string;
  withSize?: 'true' | 'false';
  genderCodeAndGenderDesc?: string;
  styleCodeAndStyleDesc?: string;
};

export type PDFParams = {
  season: string;
  seasonLine: string;
  zone: string;
  nodeName?: string;
  lang: string;
};

class ProductDataService {
  private http = fetch();

  private ep = '/v1/catalogue';

  public async getMenu(season: string, marketLine: string) {
    return this.http.get<CatalogueMenu[]>(`${this.ep}/${season}/${marketLine}`);
  }

  public async getProducts(params: ProductParams) {
    const { season, marketLine, genre, category, zone, warehouse } = params;

    return this.http.get<ProductType[]>(
      `${this.ep}/${season}/${marketLine}/${genre}/${category}`,
      {
        params: {
          zone,
          warehouse: warehouse || '',
        },
      }
    );
  }

  public async getSubProducts(params: ProductParams) {
    const {
      season,
      marketLine,
      genre,
      category,
      warehouse,
      zone,
      itemCode,
      mainFabricCode,
      itemColorCode,
      styleCodeAndStyleDesc,
      genderCodeAndGenderDesc,
    } = params;

    return this.http.get<ProductType[]>(
      `${this.ep}/${season}/${marketLine}/${genre}/${category}/sub_product`,
      {
        params: {
          itemCode,
          mainFabricCode,
          styleCodeAndStyleDesc,
          genderCodeAndGenderDesc,
          itemColorCode,
          warehouse,
          zone,
        },
      }
    );
  }

  public getProductDetails(params: ProductParams) {
    const {
      season,
      marketLine,
      genre,
      category,
      itemCode,
      mainFabricCode,
      zone,
      warehouse,
      itemColorCode,
    } = params;

    return this.http.get(
      `${this.ep}/${season}/${marketLine}/${genre}/${category}/product_detail`,
      {
        params: {
          itemCode,
          mainFabricCode,
          zone,
          warehouse,
          itemColorCode,
        },
      }
    );
  }

  public getTechnicalData(params: ProductParams) {
    const {
      season,
      marketLine,
      genre,
      category,
      itemCode,
      mainFabricCode,
      zone,
      warehouse,
      itemColorCode,
      withSize,
    } = params;

    return this.http.get(
      `${this.ep}/${season}/${marketLine}/${genre}/${category}/technical_data`,
      {
        params: {
          itemCode,
          mainFabricCode,
          zone,
          warehouse,
          itemColorCode,
          withSize,
        },
      }
    );
  }

  public getPDFData(params: PDFParams, categories: string[]) {
    return this.http.post(`${this.ep}/pdf`, categories, {
      params,
    });
  }

  public getProductInfoForOrder(params: ProductParams) {
    const { season, itemCode, mainFabricCode, zone, warehouse, itemColorCode } = params;
    return this.http.get(
      `${this.ep}/productInfoForOrder?season=${season}&zone=${zone}&warehouse=${warehouse}&itemCode=${itemCode}&itemColorCode=${itemColorCode}&mainFabricCode=${mainFabricCode}`
    );
  }

  public getAvailableDeliveryCode(params: ProductParams) {
    const { season, itemCode, mainFabricCode, zone, warehouse, itemColorCode } = params;

    return this.http.get(
      `${this.ep}/availableDeliveryCode?season=${season}&zone=${zone}&warehouse=${warehouse}&itemCode=${itemCode}&itemColorCode=${itemColorCode}&mainFabricCode=${mainFabricCode}`
    );
  }

  public getAvailableColorForOrder(params: ProductParams) {
    const { season, itemCode, mainFabricCode, zone, warehouse, itemColorCode } = params;

    return this.http.get(
      `${this.ep}/availableColorForOrder?season=${season}&zone=${zone}&warehouse=${warehouse}&itemCode=${itemCode}&itemColorCode=${itemColorCode}&mainFabricCode=${mainFabricCode}`
    );
  }

  public async search(params: { season: string; seasonLine: string; q: string }) {
    return this.http.get(`${this.ep}/search`, {
      params: {
        season: params.season,
        seasonLine: params.seasonLine,
        q: params.q,
      },
    });
  }
}

const productDataService = new ProductDataService();

export { productDataService };
